import { GetBaseURL } from "./shared";
import { ElementInfo } from "./controls";
import { OnGraphic, PushPop } from "./p5/p5_helper";

export interface CalculateCanvasSizeArgs extends DisplayInfoArgs {
	contentWidth: number;
	contentHeight: number;
	contentPadding: number;
}

interface CanvasSizeResult {
	width: number;
	height: number;
	startX: number;
	startY: number;
	workWidth: number;
	workHeight: number;
}

export function CalculateCanvasSize({
	contentHeight,
	contentPadding,
	contentWidth,
	footerArgs,
	titleArgs,
}: CalculateCanvasSizeArgs): CanvasSizeResult {
	const paddedContentWidth = contentWidth + contentPadding * 2;
	const paddedContentHeight = contentHeight + contentPadding * 2;
	const [footerWidth, footerHeight] = CalculateFooterSize(footerArgs);
	const [titleWidth, titleHeight] = CalculateTitleSize(titleArgs);

	const width = Math.max(paddedContentWidth, footerWidth, titleWidth);
	const height = paddedContentHeight + footerHeight + titleHeight;

	const startX = (width - contentWidth) / 2;
	const startY = titleHeight + contentPadding;

	return {
		width,
		height,
		startX,
		startY,
		workWidth: contentWidth,
		workHeight: contentHeight,
	};
}

interface DisplayInfoArgs {
	titleArgs: TitleArgs;
	footerArgs: FooterArgs;
}

export function DisplayInfo({ titleArgs, footerArgs }: DisplayInfoArgs, dst?: p5.Graphics|undefined) {
	OnGraphic((g)=>{
		DisplayTitle(titleArgs, g);
		DisplayFooter(footerArgs, g);
	}, dst)
}

interface TitleArgs {
	title: string;
	fontSize: number;
	titleGap: number;
}

function CalculateTitleSize({
	title,
	fontSize,
	titleGap,
}: TitleArgs): [number, number] {
	const tWidth = PushPop(() => {
		textStyle("bold");
		textSize(fontSize);
		return textWidth(title);
	});

	return [tWidth, fontSize + titleGap * 2];
}

function DisplayTitle({ title, fontSize, titleGap }: TitleArgs, g: p5.Graphics) {
	PushPop(() => {
		g.textStyle("bold");
		g.textSize(fontSize);
		g.textAlign("center", "bottom");
		g.text(title, width / 2, fontSize + titleGap);
	}, g);
}

interface FooterArgs {
	fontSize: number;
	urlGap: number;
	margin: number;
	controls: ElementInfo<string, any>[];
}

function CalculateFooterSize({
	fontSize,
	urlGap,
	margin,
	controls,
}: FooterArgs): [number, number] {
	const y = urlGap + fontSize * (controls.length + 1);
	const x = PushPop(() => {
		textSize(fontSize);
		return textWidth(GetBaseURL()) + margin * 2;
	});

	return [x, y];
}

function DisplayFooter({ fontSize, urlGap, margin, controls }: FooterArgs, g: p5.Graphics) {
	PushPop(() => {
		g.textSize(fontSize);
		g.textAlign("right", "bottom");
		g.text(GetBaseURL(), width - margin, height);

		g.textAlign("left", "bottom");
		controls.forEach((v, i) => {
			const h = height - urlGap - fontSize * (i + 1);
			g.text(`${v.Id}: ${v.Element.value}`, margin, h);
		});
	}, g);
}
